// Shared user profile dropdown (ADO — consistent header menu across pages).
//
// Self-contained: reads the signed-in user and signOut from AuthContext, so any
// header can drop in <UserMenu /> with no prop threading. Previously this menu
// lived only in DashboardPage; the Learn page showed a dead avatar+chevron, the
// Solution page had no user menu, and the Flow Builder had a different set of
// items (Home / Learn / Sign Out).
//
// Props:
//   • variant  — 'light' (default) or 'dark' for dark headers (Solution page).
//   • navGuard — optional (go) => void wrapper around navigation. The Flow
//     Builder passes its tryLeave() so navigating away can prompt on unsaved
//     changes. Default runs the navigation immediately.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  LogOut,
  UserCircle2,
  Settings,
  Library,
  Receipt,
} from 'lucide-react';
import { LEGAL_LINKS } from '@/lib/legalLinks';

const displayNameFromUser = (user) =>
  user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'there';

const emailFromUser = (user) => user?.email || '';

const firstInitial = (user) =>
  (displayNameFromUser(user).charAt(0) || 'S').toUpperCase();

export const UserMenu = ({ variant = 'light', navGuard = (go) => go() }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const go = (path) => navGuard(() => navigate(path));

  const handleSignOut = () =>
    navGuard(async () => {
      await signOut();
      navigate('/login');
    });

  const comingSoon = (feature) =>
    toast(`${feature} is on the roadmap`, { description: 'This page is coming soon.' });

  const triggerClasses =
    variant === 'dark'
      ? 'flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/30'
      : 'flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200';

  const chevronClasses =
    variant === 'dark' ? 'h-4 w-4 text-white/50' : 'h-4 w-4 text-slate-500';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={triggerClasses} data-testid="user-menu-trigger">
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.user_metadata?.avatar_url} />
          <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-semibold">
            {firstInitial(user)}
          </AvatarFallback>
        </Avatar>
        <ChevronDown className={chevronClasses} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-3 py-2.5">
          <p className="text-sm font-semibold text-slate-900 truncate">{displayNameFromUser(user)}</p>
          <p className="text-xs text-slate-500 truncate">{emailFromUser(user)}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => go('/profile')} className="cursor-pointer">
          <UserCircle2 className="h-4 w-4 mr-2 text-slate-500" />
          My Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => comingSoon('Account Settings')} className="cursor-pointer">
          <Settings className="h-4 w-4 mr-2 text-slate-500" />
          Account Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => go('/dashboard')} className="cursor-pointer">
          <Library className="h-4 w-4 mr-2 text-slate-500" />
          My Courses
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => go('/orders')} className="cursor-pointer">
          <Receipt className="h-4 w-4 mr-2 text-slate-500" />
          My Orders
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer text-red-600 focus:text-red-700"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
        {/* Legal pages open in a new tab, so unsaved work here is never left. */}
        <div className="px-3 pt-2 pb-1.5 flex items-center gap-3 text-[11px] text-slate-400" data-testid="user-menu-legal">
          {LEGAL_LINKS.map((l) => (
            <a key={l.to} href={l.to} target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 hover:underline">
              {l.label}
            </a>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
